// Copyright (c) 2022 by Move Closer

import { Authentication, AuthServiceType } from '@movecloser/front-core'
import { Component, Mixins, Watch } from 'vue-property-decorator'

import { CartIdentifier } from '../../../../contexts/checkout/contracts'
import { Inject, logger } from '../../../../support'

import { AuthMixin } from '../../../auth/shared/mixins/authMixin'
import { IAuthMixin, UserModel } from '../../../auth/shared/contracts'
import { IToastMixin, ToastMixin } from '../../../shared/mixins/toast.mixin'
import { CART_ID_KEY } from '../../services/cart'

import { CartMutationTypes } from '../../contracts'

import { BaseCartMixin, IBaseCart } from './base-cart.mixin'

enum CartInitState {
  AuthWithCart,
  AuthNoCart,
  GuestWithCart,
  GuestNoCart
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
@Component<InitCartMixin>({
  name: 'InitCartMixin',
  mounted (): void {
    this.initCartOnMount()
  }
})
export class InitCartMixin extends Mixins<IBaseCart, IAuthMixin, IToastMixin>(BaseCartMixin, AuthMixin, ToastMixin) {
  @Inject(AuthServiceType)
  protected readonly authService!: Authentication<UserModel>

  /**
   * Tries to init cart on mounted.
   */
  protected async initCartOnMount (): Promise<void> {
    // Retrieve form local storage cart identifier.
    const cartId: CartIdentifier | null = window.localStorage.getItem(CART_ID_KEY)

    if (!this.isWaitingForAuth && !this.isLoggedInUser) {
      this.$store.commit(CartMutationTypes.SetLoading, true)
      // If there is no user but there is cartId
      try {
        if (cartId) {
          const cart = await this.cartService.resolveCart(cartId, 5)
          this.$store.commit(CartMutationTypes.SetCartId, cart.id)
          this.$store.commit(CartMutationTypes.SetCart, cart)

          if (cartId !== cart.id) {
            localStorage.setItem(CART_ID_KEY, cart.id)
          }
        } else {
          // If there is no user and no cart Id
          await this.createCart()
        }
      } catch (e) {
        logger(e, 'warn')
        this.showToast((e as Error).message, 'danger')
      }

      this.$store.commit(CartMutationTypes.SetLoading, false)
    }
  }

  @Watch('isLoggedInUser')
  protected async initializeCart (isAuthenticated: boolean, oldValue: boolean): Promise<void> {
    if (
      this.$store.getters['checkout/registering'] ||
      typeof window === 'undefined' || isAuthenticated === oldValue
    ) {
      return
    }

    if (!isAuthenticated && oldValue) {
      this.deleteCart()
    }

    // Retrieve form local storage cart identifier.
    const cartId: CartIdentifier | null = window.localStorage.getItem(CART_ID_KEY)
    const state: CartInitState = isAuthenticated
      ? (cartId ? CartInitState.AuthWithCart : CartInitState.AuthNoCart)
      : (cartId ? CartInitState.GuestWithCart : CartInitState.GuestNoCart)

    this.$store.commit(CartMutationTypes.SetLoading, true)

    try {
      switch (state) {
        case CartInitState.AuthWithCart: {
          let customerCart = await this.cartService.loadCustomerCart()

          if (cartId !== customerCart.id) {
            customerCart = await this.cartService.mergerCarts(cartId as string, customerCart.id)
          }

          this.$store.commit(CartMutationTypes.SetCartId, customerCart.id)
          this.$store.commit(CartMutationTypes.SetCart, customerCart)

          localStorage.setItem(CART_ID_KEY, customerCart.id)
        }
          break
        case CartInitState.AuthNoCart:
          await this.createCustomerCart()
          break
        case CartInitState.GuestWithCart:
          await this.loadCart(cartId as string)
          break
        case CartInitState.GuestNoCart:
          await this.createCart()
      }
    } catch (e) {
      logger(e, 'warn')
      this.showToast((e as Error).message, 'danger')
      await this.createCart()
    }

    this.$store.commit(CartMutationTypes.SetLoading, false)
  }
}
